import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PenLine, Check, Download, Eraser, ShieldCheck, QrCode as QrCodeIcon, ExternalLink, Loader2, FileSignature } from "lucide-react";
import api, { apiError, openPdf } from "../lib/api";

// Panneau de signature électronique — utilisé pour Restaurateur ET Livreur.
// Prop `party` : "restaurateur" (défaut) | "driver". Base path calculée automatiquement.
// Aucun OTP — signature simple au doigt/souris, validation immédiate côté serveur.
export default function SignRestoContract({ lang = "fr", party = "restaurateur" }) {
  const basePath = party === "driver" ? "/driver/contract" : "/restaurateur/contract";
  const previewPath = party === "driver" ? "/driver/contract/preview.pdf" : `/restaurateur/documents/contract.pdf?lang=${lang}`;
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [contract, setContract] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get(`${basePath}/me`);
      setContract(data.signed ? data.contract : null);
    } catch {
      setContract(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [party]);

  // ---- Dessin canvas (souris + tactile) ----
  const getPos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault(); drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const p = getPos(e);
    ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#1A1A1A"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke();
    hasDrawn.current = true;
  };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasDrawn.current = false;
  };

  const doSign = async () => {
    if (!hasDrawn.current) { toast.error(lang === "nl" ? "Teken eerst uw handtekening" : "Dessinez d'abord votre signature"); return; }
    setSigning(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      const { data } = await api.post(`${basePath}/sign`, { signature_data_url: signature, lang });
      toast.success(lang === "nl" ? "Contract elektronisch ondertekend ✅" : "Contrat signé électroniquement ✅");
      await load();
      try { await openPdf(`${basePath}/me.pdf`, "contrat-nold-go-signe.pdf"); } catch { /* ignore */ }
      if (data?.verify_url) setContract((c) => ({ ...(c || {}), verify_url: data.verify_url }));
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSigning(false); }
  };

  if (loading) {
    return (
      <div data-testid="rt-contract-loading" className="rounded-3xl bg-white border border-[#E2DFD8] p-6 flex items-center gap-2 text-sm text-[#75807A]">
        <Loader2 className="w-4 h-4 animate-spin" /> {lang === "nl" ? "Laden..." : "Chargement..."}
      </div>
    );
  }

  // -------------------- ÉTAT SIGNÉ --------------------
  if (contract) {
    const signedDate = new Date(contract.signed_at).toLocaleString(lang === "nl" ? "nl-BE" : "fr-BE",
      { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const verifyUrl = contract.verify_url || `${window.location.origin}/verify/contract/${contract.id}`;
    return (
      <section data-testid="rt-contract-signed" className="rounded-3xl bg-white border-2 border-[#16a34a]/40 p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-2xl grid place-items-center bg-[#DCFCE7]">
            <ShieldCheck className="w-6 h-6 text-[#16a34a]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">
              {lang === "nl" ? "Contract elektronisch ondertekend" : "Contrat signé électroniquement"}
            </h3>
            <p className="text-sm text-[#75807A] mt-1">
              {lang === "nl" ? "Ondertekend op" : "Signé le"} <b>{signedDate}</b> ·
              &nbsp;ID <span className="font-mono text-[#F26419]">{contract.hash_short}</span>
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center bg-[#FFF3E6] rounded-2xl p-4 border border-[#FFD9B3]">
          <div className="min-w-0">
            <div className="text-xs text-[#7A3A00] font-bold uppercase tracking-wide flex items-center gap-1">
              <QrCodeIcon className="w-3.5 h-3.5" /> {lang === "nl" ? "Openbare verificatie QR" : "QR de vérification publique"}
            </div>
            <a href={verifyUrl} target="_blank" rel="noreferrer"
              className="mt-1 block font-mono text-xs text-[#1A1A1A] break-all hover:underline"
              data-testid="rt-contract-verify-url">
              {verifyUrl}
            </a>
            <p className="text-[11px] text-[#7A3A00] mt-2 leading-relaxed">
              {lang === "nl"
                ? "Deze QR-code staat op elk uitgeprinte contract en bewijst de authenticiteit."
                : "Le QR est imprimé sur chaque contrat téléchargé et prouve l'authenticité."}
            </p>
          </div>
          <img
            alt="QR"
            width="120" height="120"
            className="rounded-xl border border-white bg-white shrink-0"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(verifyUrl)}`}
          />
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            data-testid="rt-contract-download-signed"
            onClick={async () => {
              try { await openPdf("/restaurateur/contract/me.pdf", "contrat-nold-go-signe.pdf"); }
              catch { toast.error(lang === "nl" ? "Kan PDF niet openen" : "Impossible d'ouvrir le PDF"); }
            }}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm">
            <Download className="w-4 h-4" /> {lang === "nl" ? "Ondertekend contract" : "Contrat signé (PDF)"}
          </button>
          <a
            data-testid="rt-contract-open-verify"
            href={verifyUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] text-[#1A1A1A] font-bold text-sm">
            <ExternalLink className="w-4 h-4" /> {lang === "nl" ? "Verificatiepagina" : "Page de vérification"}
          </a>
        </div>
      </section>
    );
  }

  // -------------------- ÉTAT NON SIGNÉ --------------------
  return (
    <section data-testid="rt-contract-signblock" className="rounded-3xl bg-white border-2 border-[#F26419]/40 p-6">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-11 h-11 rounded-2xl grid place-items-center bg-[#FFF3E6]">
          <FileSignature className="w-6 h-6 text-[#F26419]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-head font-black text-xl text-[#1A1A1A]">
            {party === "driver"
              ? (lang === "nl" ? "Onderteken uw koeriercontract" : "Signer mon contrat livreur")
              : (lang === "nl" ? "Onderteken uw partnerschapscontract" : "Signer mon contrat de partenariat")}
          </h3>
          <p className="text-sm text-[#75807A] mt-1">
            {lang === "nl"
              ? "Lees eerst het contract en teken vervolgens met uw vinger of muis in het onderstaande vak. Elektronische ondertekening, geldig in België."
              : "Lisez d'abord le contrat, puis signez au doigt (ou à la souris) dans le cadre ci-dessous. Signature électronique, valable en Belgique."}
          </p>
        </div>
      </div>

      <button
        data-testid="rt-contract-preview"
        onClick={async () => {
          try { await openPdf(previewPath, "contrat-nold-go.pdf"); }
          catch { toast.error(lang === "nl" ? "Kan PDF niet openen" : "Impossible d'ouvrir le PDF"); }
        }}
        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F26419] hover:text-[#D94D0E]"
      >
        <Download className="w-4 h-4" /> {lang === "nl" ? "Contract lezen (PDF)" : "Lire le contrat (PDF)"}
      </button>

      <div className="mt-4">
        <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] bg-[#FDF8F0] overflow-hidden">
          <canvas
            ref={canvasRef}
            data-testid="rt-contract-canvas"
            width={800} height={220}
            className="w-full touch-none"
            style={{ height: 200, cursor: "crosshair" }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <button
            data-testid="rt-contract-clear"
            onClick={clearCanvas}
            className="inline-flex items-center gap-1 text-xs text-[#75807A] hover:text-[#D32F2F]">
            <Eraser className="w-3.5 h-3.5" /> {lang === "nl" ? "Wissen" : "Effacer"}
          </button>
          <div className="text-[11px] text-[#75807A]">
            {lang === "nl" ? "Uw handtekening wordt versleuteld opgeslagen." : "Signature stockée de façon chiffrée."}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid="rt-contract-submit"
          onClick={doSign}
          disabled={signing}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm disabled:opacity-60"
        >
          {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
          {signing
            ? (lang === "nl" ? "Ondertekenen..." : "Signature en cours...")
            : (lang === "nl" ? "Ondertekenen en bevestigen" : "Signer et confirmer")}
        </button>
        <span className="inline-flex items-center gap-1 text-xs text-[#75807A]">
          <Check className="w-3.5 h-3.5 text-[#16a34a]" />
          {lang === "nl" ? "eIDAS-compatibel" : "conforme eIDAS"}
        </span>
      </div>
    </section>
  );
}
